import { useEffect, useState } from "react"
import { router } from "expo-router"
import CalculateCard from "@/components/analysis/calculate/CalculateCard"
import ToolTips from "@/constants/ToolTips"
import FormDivider from "@/components/forms/FormDivider"
import { useEditAnalysis } from "@/queries/analysis"
import { useUser } from "@/queries/user"
import CalculatorItem from "@/components/calculate/CalculatorItem"
import { useStore } from "@nanostores/react"
import { currentStrategy } from "@/stores/strategyStore"
import { type AnalysisType } from "@/generated/graphql"
import * as AnalyzerEvents from "@/analytics/events/analyzer"

interface SummarySectionProps {
  analysis?: AnalysisType | null
}

const SummarySection: React.FC<SummarySectionProps> = ({
  analysis,
}: SummarySectionProps) => {
  const strategy = useStore(currentStrategy)
  const [arv, setArv] = useState<number | null | undefined>()
  const [purchasePrice, setPurchasePrice] = useState<
    number | null | undefined
  >()
  const [rehabCost, setRehabCost] = useState<number | null | undefined>()
  const { data: user } = useUser()
  const editAnalysisMutation = useEditAnalysis()

  useEffect(() => {
    if (analysis) {
      setArv(analysis.afterRepairValue)
      setPurchasePrice(analysis.purchasePrice)
      setRehabCost(analysis.rehabCost)
    }
  }, [analysis])

  const arvPerSqFt =
    arv && analysis?.area ? `($${Math.round(arv / analysis.area)}/Sq Ft)` : ""

  const purchasePricePerSqFt =
    purchasePrice && analysis?.area
      ? `($${Math.round(purchasePrice / analysis.area)}/Sq Ft)`
      : ""

  const rehabPerSqFt =
    rehabCost && analysis?.area
      ? `($${Math.round(rehabCost / analysis.area)}/Sq Ft)`
      : ""

  if (!analysis || !user) {
    return null
  }

  const handleBlur = (title: string, value: number | null | undefined) => {
    AnalyzerEvents.analysisEditSubmitted({
      analysis,
      editedField: {
        fieldName: title,
        newValue: value,
      },
      fromScreen: "Subject Property Calculate",
      toScreen: "Subject Property Calculate",
    })
    editAnalysisMutation.mutate({
      analysisId: analysis?.id,
      analysisInput: {
        afterRepairValue: arv,
        purchasePrice,
        rehabCost,
      },
    })
  }

  return (
    <CalculateCard title="Summary">
      <CalculatorItem
        title="Estimated ARV"
        subtitle={arvPerSqFt}
        value={arv}
        tooltip={ToolTips.estimatedARV}
        onBlur={() => {
          handleBlur("Estimated ARV", arv)
        }}
        onValueChange={setArv}
      />
      <CalculatorItem
        title="Purchase Price"
        subtitle={purchasePricePerSqFt}
        value={purchasePrice}
        tooltip={ToolTips.purchasePrice}
        onBlur={() => {
          handleBlur("Purchase Price", purchasePrice)
        }}
        onValueChange={setPurchasePrice}
      />
      <CalculatorItem
        title="Estimated Rehab Costs"
        subtitle={rehabPerSqFt}
        value={rehabCost}
        tooltip={ToolTips.estimatedRehabCosts}
        onBlur={() => {
          handleBlur("Estimated Rehab Costs", rehabCost)
        }}
        onValueChange={setRehabCost}
      />

      {strategy === "Fix & Flip" && (
        <>
          <CalculatorItem
            title="Cost Basis"
            value={analysis.totalCostBasis}
            tooltip={ToolTips.costBasis}
            noMin
          />
          <CalculatorItem
            title="Gross Profit Potential"
            value={analysis.grossProfitPotential}
            tooltip={ToolTips.grossProfitPotential}
            noMin
          />

          <FormDivider />

          <CalculatorItem
            title="Net Profit Potential"
            value={analysis.profitPotential}
            tooltip={ToolTips.netProfitPotential}
            noMin
          />

          <CalculatorItem
            title="Rewards Credits"
            value={analysis.rewardsCredit}
            tooltip={ToolTips.rewardsCredits}
            prefix={"-$"}
            blankText="Get Rewards"
            onBlankPress={() => {
              router.push("/rewards")
            }}
          />

          <CalculatorItem
            title="Estimated Cash to Close"
            value={analysis.cashToClose}
            tooltip={ToolTips.estimatedCashToClose}
          />
        </>
      )}
    </CalculateCard>
  )
}

export default SummarySection
